// RUTA: src/components/PwaUpdateBanner.tsx
//
// Banner de respaldo que aparece cuando hay una versión nueva del
// PWA disponible y el usuario no la ha recibido todavía.
//
// ESTRATEGIA HÍBRIDA — auto-update silencioso + banner manual:
//
//   1. AUTOMÁTICO (caso 90% de los clientes):
//      vite-plugin-pwa con registerType: 'autoUpdate' + skipWaiting:
//      true se encarga solo. Cuando hay nueva versión, el SW se
//      activa silenciosamente en background. La próxima vez que el
//      usuario abre la app, obtiene la versión nueva sin notar.
//
//   2. MANUAL DE RESPALDO (este componente):
//      Para clientes que dejan la PWA abierta indefinidamente sin
//      cerrarla, el SW puede activar la nueva versión PERO el JS en
//      memoria es el viejo. Esos clientes no ven mejoras hasta que
//      recargan.
//
//      Aquí escuchamos el evento `controllerchange` del SW (que se
//      dispara cuando un nuevo SW toma control) Y también
//      `useRegisterSW` para el caso prompt-style. Cuando detectamos
//      cualquiera, mostramos el banner como AYUDA opcional. Click en
//      "Actualizar ahora" llama updateServiceWorker(true) y/o
//      window.location.reload() para forzar recarga.
//
//      El banner es descartable — si el cliente lo cierra, no vuelve
//      a aparecer en esa sesión (a menos que se detecte otra
//      versión nueva más adelante).

import { useEffect, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { useRegisterSW } from 'virtual:pwa-register/react';

// Polling cada 15 min como red de seguridad para sesiones largas
// online sin moverse. Antes era 30 min — bajamos para detectar
// updates antes en uso normal. El costo de red es trivial (HEAD
// request al SW).
const POLL_INTERVAL_MS = 15 * 60 * 1000;

export function PwaUpdateBanner() {
  const [dismissed, setDismissed] = useState(false);
  const [controllerChanged, setControllerChanged] = useState(false);

  // Guard: no registrar SW en iframes (Lovable preview, etc.)
  const isInIframe = (() => {
    try { return window.self !== window.top; } catch { return true; }
  })();
  const isPreviewHost = typeof window !== 'undefined' && (
    window.location.hostname.includes('id-preview--') ||
    window.location.hostname.includes('lovableproject.com')
  );
  const skipPwa = isInIframe || isPreviewHost;

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: !skipPwa,
    onRegisteredSW(_swUrl, registration) {
      if (!registration || skipPwa) return;

      const checkForUpdate = () => {
        registration.update().catch(() => { /* ignore */ });
      };

      // Check inmediato al montar — crítico para PWAs instaladas en
      // home screen (iOS especialmente). Antes solo se revisaba cada
      // N min: si el cliente abría la PWA, no detectaba update y
      // cerraba antes del polling, se quedaba con la versión vieja
      // para siempre. Ahora cada cold-start dispara el check.
      checkForUpdate();

      // Polling como red de seguridad para sesiones largas abiertas.
      setInterval(checkForUpdate, POLL_INTERVAL_MS);

      // Check al volver a la app (foreground / visibility) — caso
      // típico: usuario abrió la PWA hace rato, switch a otra app,
      // regresa. Si se desplegó nueva versión mientras estaba
      // afuera, queremos detectarla AL REGRESAR, no esperar al
      // siguiente polling.
      const checkOnReturn = () => {
        if (document.visibilityState === 'visible') checkForUpdate();
      };
      document.addEventListener('visibilitychange', checkOnReturn);
      window.addEventListener('focus', checkOnReturn);

      // Check al recuperar conexión — escenario clave que motivó
      // este cambio. Joel describió: "Cliente está en el gym sin
      // señal cuando hago deploy. Recupera señal al salir del cuarto
      // → debería detectar el update INMEDIATAMENTE en vez de
      // esperar al próximo polling de 15 min". Disparamos el check
      // en el evento exacto, no probabilísticamente.
      window.addEventListener('iftmx:reconnected', checkForUpdate);
    },
    onRegisterError(err) {
      console.warn('[PWA] SW registration error:', err);
    },
  });

  // Listener manual de 'controllerchange' — backup detection.
  // Con registerType=autoUpdate + skipWaiting=true, el SW nuevo toma
  // control automáticamente y este evento se dispara. Ahí ofrecemos
  // al usuario forzar recarga para aplicar el JS fresco.
  useEffect(() => {
    if (skipPwa || !('serviceWorker' in navigator)) return;
    const handler = () => {
      // Si esto es la primera carga (no había controller previo),
      // ignoramos el evento — eso solo significa que el SW se acaba
      // de instalar, no que sea una actualización.
      if (!navigator.serviceWorker.controller) return;
      setControllerChanged(true);
    };
    navigator.serviceWorker.addEventListener('controllerchange', handler);
    return () => navigator.serviceWorker.removeEventListener('controllerchange', handler);
  }, [skipPwa]);

  // Aparecer si needRefresh O si detectamos controllerchange — el
  // primero cubre prompt-mode, el segundo autoUpdate+skipWaiting.
  const shouldShow = (needRefresh || controllerChanged) && !dismissed && !skipPwa;

  // Reset dismissed cuando se detecta nueva actualización (después
  // de que el cliente cerró el banner anterior).
  useEffect(() => {
    if (needRefresh || controllerChanged) setDismissed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needRefresh, controllerChanged]);

  if (!shouldShow) return null;

  const handleUpdate = () => {
    // Estrategia robusta:
    //   1. Si hay un SW waiting, updateServiceWorker(true) lo activa
    //      con skipWaiting + reload.
    //   2. Si el SW ya tomó control (controllerchange), basta con
    //      window.location.reload() para que el JS en memoria se
    //      refresque a la versión nueva.
    void updateServiceWorker(true).catch(() => {
      window.location.reload();
    });
    // Failsafe: si updateSW no recarga en 1.5s, forzamos reload.
    setTimeout(() => window.location.reload(), 1500);
  };

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-50 px-3"
      style={{ top: 'max(env(safe-area-inset-top), 0.5rem)' }}
      role="alert"
      aria-live="polite"
    >
      <div className="rounded-2xl border-2 border-accent/60 bg-card shadow-2xl shadow-accent/20 p-3 flex items-center gap-3 max-w-md">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-accent/30 to-amber-500/30 flex items-center justify-center shrink-0 animate-pulse-soft">
          <Sparkles className="w-4 h-4 text-accent" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-black uppercase tracking-widest text-accent">
            Nueva versión disponible
          </p>
          <p className="text-xs text-muted-foreground leading-tight mt-0.5">
            Actualiza para aplicar las últimas mejoras
          </p>
        </div>
        <button
          onClick={handleUpdate}
          className="shrink-0 px-3 py-2 rounded-lg bg-accent hover:bg-accent/90 text-accent-foreground text-[11px] font-black uppercase tracking-wider transition-colors min-h-[36px]"
        >
          Actualizar
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="shrink-0 w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Cerrar aviso"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
